import dayjs from "dayjs";
import "dayjs/locale/es";
import {IconGift} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import useGetBalanceBonus from "@/hooks/wallet/useGetBalanceBonus";
import {formatCurrencyAmount} from "@/util/balance.util";
import styles from "./BalanceBonus.module.css";

const formatExpiry = (expiresAt, language) => {
  if (!expiresAt) return null;
  const parsed = dayjs(expiresAt);
  if (!parsed.isValid()) return null;

  const isSpanish = (language || "es").toLowerCase().startsWith("es");
  if (isSpanish) {
    return parsed.locale("es").format("D [de] MMMM [de] YYYY");
  }
  return parsed.locale("en").format("MMMM D, YYYY");
};

/**
 * Compact, mobile-first card that surfaces the user's balance bonus next to the
 * withdrawable wallet balance.
 *
 * The bonus is a SEPARATE, non-withdrawable amount: it is rendered as its own
 * clearly-labelled card and is never folded into the main balance figure.
 * Renders nothing when the user has no active bonus.
 */
const BalanceBonus = () => {
  const {t, i18n} = useTranslation();
  const {bonus, hasBonus} = useGetBalanceBonus();

  if (!hasBonus || !bonus) return null;

  const {
    currency = "VES",
    status,
    formattedPlayable,
    playableCents,
    wageredCents = 0,
    wageringTargetCents = 0,
    wageringRemainingCents = 0,
    wageringMet = false,
    expiresAt,
    type,
  } = bonus;

  // Format from the raw cents with the app's shared formatter so the figure
  // matches the wagering line below. The wallet's `formattedPlayable` already
  // embeds its own currency symbol (e.g. "Bs347.50") — prepending another one
  // here rendered "Bs. Bs347.50" — so it is only a last-resort fallback, as-is.
  const playableDisplay = Number.isFinite(playableCents)
    ? formatCurrencyAmount(playableCents, currency)
    : formattedPlayable || "";

  const progressPct = wageringMet
    ? 100
    : wageringTargetCents > 0
      ? Math.min(100, Math.max(0, Math.round((wageredCents / wageringTargetCents) * 100)))
      : 0;

  const wageredDisplay = formatCurrencyAmount(wageredCents, currency);
  const targetDisplay = formatCurrencyAmount(wageringTargetCents, currency);
  const remainingDisplay = formatCurrencyAmount(wageringRemainingCents, currency);

  const statusLabel =
    status === "unlocked" || wageringMet ? t("balanceBonus_statusUnlocked") : t("balanceBonus_statusActive");
  const isUnlocked = status === "unlocked" || wageringMet;

  const expiryLabel = formatExpiry(expiresAt, i18n.language);

  return (
    <section
      className={styles.card}
      data-bonus-type={type || undefined}
      aria-label={t("balanceBonus_title")}
    >
      <header className={styles.head}>
        <IconGift
          size={16}
          stroke={2}
          className={styles.icon}
          aria-hidden="true"
        />
        <span className={styles.title}>{t("balanceBonus_title")}</span>
        <span className={`${styles.statusPill} ${isUnlocked ? styles.statusUnlocked : styles.statusActive}`}>
          {statusLabel}
        </span>
      </header>

      <div
        className={styles.amount}
        aria-live="polite"
      >
        {playableDisplay}
      </div>

      <p className={styles.explain}>{t("balanceBonus_explain")}</p>

      <div className={styles.progress}>
        <div className={styles.progressHead}>
          <span>{t("balanceBonus_progressLabel")}</span>
          <span className={styles.progressAmounts}>
            {wageredDisplay} / {targetDisplay}
          </span>
        </div>
        <div
          className={styles.track}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
          aria-label={t("balanceBonus_progressLabel")}
        >
          <div
            className={`${styles.fill} ${isUnlocked ? styles.fillMet : ""}`}
            style={{width: `${progressPct}%`}}
          />
        </div>
        <p className={`${styles.progressMsg} ${isUnlocked ? styles.progressMsgMet : styles.progressMsgPending}`}>
          {isUnlocked ? t("balanceBonus_wageringMet") : t("balanceBonus_wageringRemaining", {amount: remainingDisplay})}
        </p>
      </div>

      {expiryLabel && <div className={styles.expiry}>{t("balanceBonus_expires", {date: expiryLabel})}</div>}
    </section>
  );
};

export default BalanceBonus;
